import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { ChatArtifact as Artifact } from '@/app/queries';
import {
  useCreateExercise,
  useCreateObjective,
  useSaveMicrocycle,
  useSaveSession,
} from '@/app/queries';
import { addDays, todayISO } from '@shared/dates';
import { Button } from '@/ui/Button';
import { Chip } from '@/ui/Chip';
import { Notice } from '@/ui/Panel';

/**
 * A plan the coach can take, rather than a plan he has to retype.
 *
 * The answer above it says what to do; this is that thing in the shape the app
 * stores, with one button that puts it there. It is still the coach adding it —
 * nothing is written until he presses — because a chat that files things on his
 * behalf is a chat he has to audit.
 *
 * The model supplies judgement; the app supplies facts. Which team, which date,
 * which season are filled in here from what is already on screen, so the model
 * is never asked for an id it cannot see.
 */
export function ChatArtifactCard({
  artifact,
  teamId,
  seasonId,
}: {
  artifact: Artifact;
  teamId: string | null;
  seasonId: string | null;
}) {
  const t = useTranslations();
  const [added, setAdded] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const session = useSaveSession();
  const week = useSaveMicrocycle();
  const objective = useCreateObjective();
  const exercise = useCreateExercise();

  const busy =
    session.isPending || week.isPending || objective.isPending || exercise.isPending;

  const add = async () => {
    if (!teamId) return;
    setFailed(false);
    try {
      if (artifact.kind === 'session') {
        await session.mutateAsync({
          input: {
            teamId,
            onDate: todayISO(),
            title: artifact.title,
            objective: artifact.objective,
            rationale: artifact.rationale,
            plannedDurationMin: artifact.durationMin,
            status: 'planned',
            exercises: artifact.exercises.map((e) => ({
              name: e.name,
              categoryKey: e.categoryKey,
              durationMin: e.durationMin,
              rationale: e.rationale,
              notes: e.objective,
            })),
          },
        } as never);
        setAdded(t('chat.addedSession'));
      } else if (artifact.kind === 'week') {
        const monday = todayISO();
        await week.mutateAsync({
          input: {
            teamId,
            seasonId: seasonId ?? undefined,
            name: artifact.name,
            startsOn: monday,
            endsOn: addDays(monday, 6),
            objectiveSummary: artifact.objectiveSummary,
            days: artifact.days.map((d, i) => ({
              onDate: addDays(monday, i),
              mdLabel: d.mdLabel || undefined,
              mainObjective: d.mainObjective || undefined,
              dayType: d.dayType,
              plannedDurationMin: d.plannedDurationMin || undefined,
            })),
          },
        } as never);
        setAdded(t('chat.addedWeek'));
      } else if (artifact.kind === 'objective') {
        await objective.mutateAsync({
          teamId,
          seasonId: seasonId ?? undefined,
          level: artifact.level,
          dimension: artifact.dimension,
          title: artifact.title,
          detail: artifact.detail || undefined,
        } as never);
        setAdded(t('chat.addedObjective'));
      } else {
        await exercise.mutateAsync({
          name: artifact.name,
          categoryKey: artifact.categoryKey,
          durationMin: artifact.durationMin,
          rationale: artifact.rationale,
          minPlayers: artifact.minPlayers,
          maxPlayers: artifact.maxPlayers,
          areaLength: artifact.areaLength,
          areaWidth: artifact.areaWidth,
          workSeconds: artifact.workSeconds,
          restSeconds: artifact.restSeconds,
        } as never);
        setAdded(t('chat.addedExercise'));
      }
    } catch {
      setFailed(true);
    }
  };

  return (
    <div className="mt-2 overflow-hidden rounded-(--radius-panel) border border-signal bg-surface">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <Chip>{t(`chat.kind_${artifact.kind}`)}</Chip>
        <span className="min-w-0 flex-1 truncate font-medium">{titleOf(artifact)}</span>
      </header>

      <div className="grid gap-2 px-4 py-3 text-meta">
        {artifact.kind === 'session' ? (
          <>
            <p className="text-ink-muted">{artifact.objective}</p>
            <ul className="grid gap-1">
              {artifact.exercises.map((e, i) => (
                <li key={`${e.name}-${i}`} className="flex flex-wrap items-baseline gap-2">
                  <span className="tabular w-12 text-ink-faint">
                    {t('ai.minutes', { count: e.durationMin })}
                  </span>
                  <span className="font-medium">{e.name}</span>
                  <span className="min-w-0 flex-1 text-ink-faint">{e.objective}</span>
                </li>
              ))}
            </ul>
            <p className="tabular text-ink-faint">
              {t('ai.totalMinutes', {
                count: artifact.exercises.reduce((n, e) => n + e.durationMin, 0),
                planned: artifact.durationMin,
              })}
            </p>
          </>
        ) : null}

        {artifact.kind === 'week' ? (
          <>
            <p className="text-ink-muted">{artifact.objectiveSummary}</p>
            <ul className="grid gap-1">
              {artifact.days.map((d, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-2">
                  <span className="tabular w-14 font-medium">{d.mdLabel || '—'}</span>
                  <Chip muted>{t(`plan.dayType_${d.dayType}`)}</Chip>
                  <span className="min-w-0 flex-1">{d.mainObjective}</span>
                  <span className="tabular text-ink-faint">
                    {d.plannedDurationMin ? t('ai.minutes', { count: d.plannedDurationMin }) : ''}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {artifact.kind === 'objective' ? (
          <>
            <div className="flex flex-wrap gap-2">
              <Chip muted>{t(`development.dimension.${artifact.dimension}`)}</Chip>
              <Chip muted>{artifact.level}</Chip>
            </div>
            {artifact.detail ? <p className="text-ink-muted">{artifact.detail}</p> : null}
          </>
        ) : null}

        {artifact.kind === 'exercise' ? (
          <>
            <p className="text-ink-muted">{artifact.rationale}</p>
            <p className="tabular text-ink-faint">
              {t('ai.minutes', { count: artifact.durationMin })}
              {artifact.minPlayers ? ` · ${artifact.minPlayers}–${artifact.maxPlayers ?? artifact.minPlayers}` : ''}
              {artifact.areaLength ? ` · ${artifact.areaLength}×${artifact.areaWidth ?? ''}m` : ''}
            </p>
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2">
        {added ? (
          <span className="text-meta text-ok">{added}</span>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="!min-h-8 !text-meta"
            busy={busy}
            disabled={!teamId}
            onClick={() => void add()}
          >
            {t(`chat.add_${artifact.kind}`)}
          </Button>
        )}
        {failed ? <Notice tone="risk">{t('common.saveFailed')}</Notice> : null}
        <span className="text-meta text-ink-faint">{t('chat.addHint')}</span>
      </div>
    </div>
  );
}

function titleOf(a: Artifact): string {
  if (a.kind === 'session') return a.title;
  if (a.kind === 'week') return a.name;
  if (a.kind === 'objective') return a.title;
  return a.name;
}
